import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
  GraduationCap,
  Lock,
  Mail,
  Phone,
  User,
  UserPlus,
} from "lucide-react";
import React, { memo, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Composant InputField séparé pour éviter les re-rendus
const InputField = memo(
  ({
    name,
    label,
    type = "text",
    icon: Icon,
    placeholder,
    required = false,
    value,
    onChange,
    error,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`block w-full pl-10 pr-10 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
            error
              ? "border-red-300 bg-red-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
        />
        {name === "mot_de_passe" && (
          <button
            type="button"
            onClick={setShowPassword}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        )}
        {name === "confirm_password" && (
          <button
            type="button"
            onClick={setShowConfirmPassword}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            {showConfirmPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        )}
      </div>
      {error && (
        <div className="flex items-center space-x-1 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
);

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    mot_de_passe: "",
    confirm_password: "",
    nom_enfant: "",
    classe_enfant: "",
    telephone: "",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom est requis";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }

    if (!formData.mot_de_passe) {
      newErrors.mot_de_passe = "Le mot de passe est requis";
    } else if (formData.mot_de_passe.length < 6) {
      newErrors.mot_de_passe =
        "Le mot de passe doit contenir au moins 6 caractères";
    }

    if (formData.mot_de_passe !== formData.confirm_password) {
      newErrors.confirm_password = "Les mots de passe ne correspondent pas";
    }

    if (!formData.nom_enfant.trim()) {
      newErrors.nom_enfant = "Le nom de l'enfant est requis";
    }

    if (!formData.classe_enfant.trim()) {
      newErrors.classe_enfant = "La classe de l'enfant est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    setLoading(true);
    try {
      // Créer l'utilisateur
      const userResponse = await fetch(
        "http://localhost:8000/api/auth/register",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            nom: formData.nom,
            email: formData.email,
            mot_de_passe: formData.mot_de_passe,
            role: "parent",
          }),
        }
      );

      if (!userResponse.ok) {
        const errorData = await userResponse.json();
        console.error("Registration error response:", errorData);

        if (
          userResponse.status === 400 &&
          errorData.detail === "Email already registered"
        ) {
          throw new Error(
            "Cette adresse email est déjà utilisée. Veuillez utiliser une autre adresse ou vous connecter."
          );
        }

        throw new Error(
          errorData.detail || "Erreur lors de la création du compte"
        );
      }

      const userData = await userResponse.json();
      console.log("User data received:", userData); // Debug log

      // Créer le profil parent
      const parentResponse = await fetch("http://localhost:8000/api/parents", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userData.access_token}`,
        },
        body: JSON.stringify({
          user_id: userData.user?.id || userData.id,
          nom_enfant: formData.nom_enfant,
          classe_enfant: formData.classe_enfant,
          telephone: formData.telephone,
        }),
      });

      if (!parentResponse.ok) {
        const parentErrorData = await parentResponse.json();
        console.error("Parent creation error:", parentErrorData);
        throw new Error(
          "Erreur lors de la création du profil parent: " +
            (parentErrorData.detail || "Erreur inconnue")
        );
      }

      toast.success("Compte créé avec succès !");

      // Se connecter automatiquement
      await login(formData.email, formData.mot_de_passe);
      // La redirection est gérée dans AuthContext
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.message || "Erreur lors de la création du compte");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [errors]
  );

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </Link>

          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
              <UserPlus className="h-8 w-8" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Créer votre compte
          </h2>
          <p className="text-gray-600">
            Rejoignez la communauté des parents engagés
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Informations personnelles */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <User className="h-6 w-6 mr-3 text-blue-600" />
                Informations personnelles
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  name="nom"
                  label="Nom complet"
                  icon={User}
                  placeholder="Votre nom complet"
                  required
                  value={formData.nom}
                  onChange={handleChange}
                  error={errors.nom}
                />

                <InputField
                  name="email"
                  label="Adresse email"
                  type="email"
                  icon={Mail}
                  placeholder="votre@email.com"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                />

                <InputField
                  name="telephone"
                  label="Téléphone"
                  type="tel"
                  icon={Phone}
                  placeholder="+237 123 456 789"
                  value={formData.telephone}
                  onChange={handleChange}
                  error={errors.telephone}
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Lock className="h-6 w-6 mr-3 text-blue-600" />
                Sécurité
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  name="mot_de_passe"
                  label="Mot de passe"
                  type={showPassword ? "text" : "password"}
                  icon={Lock}
                  placeholder="Votre mot de passe"
                  required
                  value={formData.mot_de_passe}
                  onChange={handleChange}
                  error={errors.mot_de_passe}
                  showPassword={showPassword}
                  setShowPassword={toggleShowPassword}
                />

                <InputField
                  name="confirm_password"
                  label="Confirmer le mot de passe"
                  type={showConfirmPassword ? "text" : "password"}
                  icon={Lock}
                  placeholder="Confirmez votre mot de passe"
                  required
                  value={formData.confirm_password}
                  onChange={handleChange}
                  error={errors.confirm_password}
                  showConfirmPassword={showConfirmPassword}
                  setShowConfirmPassword={toggleShowConfirmPassword}
                />
              </div>
            </div>

            {/* Informations enfant */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <GraduationCap className="h-6 w-6 mr-3 text-blue-600" />
                Informations sur votre enfant
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  name="nom_enfant"
                  label="Nom de l'enfant"
                  icon={User}
                  placeholder="Nom de votre enfant"
                  required
                  value={formData.nom_enfant}
                  onChange={handleChange}
                  error={errors.nom_enfant}
                />

                <InputField
                  name="classe_enfant"
                  label="Classe de l'enfant"
                  icon={GraduationCap}
                  placeholder="Ex: 4ème A, 3ème B"
                  required
                  value={formData.classe_enfant}
                  onChange={handleChange}
                  error={errors.classe_enfant}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all duration-200"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Création du compte...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Créer mon compte
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Vous avez déjà un compte ?{" "}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                Se connecter
              </Link>
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
          <h3 className="text-lg font-semibold mb-4">Vos avantages</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-300" />
              QR code personnel pour valider votre participation
            </li>
            <li className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-300" />
              Suivi de votre engagement en temps réel
            </li>
            <li className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-300" />
              Récompenses et certificats personnalisés
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Register;
